import React, { useEffect, useState, useCallback } from 'react'
import { Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom'
import { MainLayout } from '@/app/navigation'
import { adminTabs, adminNavigation } from './config/tabs.config'
import { ExpertiseDetailPage } from '@/ui/components/pages/expertise-detail'
import { PipelineTab } from './tabs/pipeline'
import { TasksTab } from './tabs/mes-taches'
import { UserManagementTab } from './tabs/user-management'
import { SearchTab } from './tabs/search'
import { PaymentTab } from './tabs/payment'
import { ExpertTab } from './tabs/expert'
import { AutomatisationTab } from './tabs/automatisation'
import { IATab } from './tabs/ia'
import { TrackerTab } from './tabs/tracker'
import { TrackerDetailPage } from './tabs/tracker/components/TrackerDetailPage'
import { EmailsTab } from './tabs/emails'
import { SettingsTab } from './tabs/settings/SettingsTab'
import { MonEspaceTab } from './tabs/mon-espace/MonEspaceTab'
import { TicketTab } from './tabs/ticket'
import { WhatsNewTab } from './tabs/whats-new'
import { EquipeTab } from './tabs/equipe'
import { MondayMeetingTab } from './tabs/monday-meeting'
import { BureauActivityTab } from './tabs/bureau-activity'
import { FacturationTab } from './tabs/facturation'
import { BoardsTab } from './tabs/boards'
import { SMSTab } from './tabs/sms'
import { AgentLeaTab } from './tabs/agent-lea'
import { LeaV3Tab } from './tabs/lea-v3'
import { VoipTab } from './tabs/voip'
import { AppelsTab } from './tabs/appels'
import { AffaireDetailPage } from './tabs/affaire-detail'
import { CreateDossierWizard } from './tabs/affaire-detail/components/CreateDossierWizard'
import { NewDossiersTab } from './tabs/affaire-detail/components/NewDossiersTab'
import { useUnreadCount } from './tabs/mes-taches/hooks/useUnreadCount'
import { useUnvalidatedWeeksCount } from './tabs/mon-espace/hooks/useUnvalidatedWeeksCount'
import { UnreadProvider } from './tabs/mes-taches/contexts/UnreadContext'
import { useUnpaidPaymentCount } from './tabs/payment/hooks/useUnpaidPaymentCount'
import { useTrackerUnreadCount } from './tabs/tracker/hooks/useTrackerUnreadCount'
import { TrackerUnreadProvider } from './tabs/tracker/contexts/TrackerUnreadContext'
import { useTrackerNotifications } from './tabs/tracker/hooks/useTrackerNotifications'
import { useTicketMentionCount } from './tabs/ticket/hooks'
import { useSMSUnreadCount } from './tabs/sms/hooks/useSMSUnreadCount'
import { SMSUnreadProvider } from './tabs/sms/contexts/SMSUnreadContext'
import { useAgentPendingCount } from './tabs/agent-lea/hooks/useAgentPendingCount'
import { useEmailTriageBadgeCount } from './tabs/emails/hooks/useEmailTriageBadgeCount'
import { useVoicemailUnreadCount } from './tabs/appels/hooks/useVoicemailUnreadCount'

// Wrapper component to extract reference from URL params
const ExpertiseDetailPageWrapper: React.FC = () => {
  const { reference } = useParams<{ reference: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we have expert context to return to their dossiers view
  const expertContext = location.state?.expertContext;
  const searchContext = location.state?.searchContext;
  const backUrl = location.state?.from || '/admin/pipeline';

  // Custom back handler that restores expert context if available
  const handleBack = () => {
    if (expertContext && backUrl === '/admin/expert') {
      // Navigate back to expert tab with the selected expert
      navigate('/admin/expert', {
        state: {
          selectedExpert: expertContext,
          viewMode: 'dossiers'
        }
      });
    } else if (searchContext && backUrl === '/admin/search') {
      // Navigate back to search tab with the search context preserved
      const navState: any = {
        tab: searchContext.tab,
        searchName: searchContext.searchQuery
      };

      // If there's a selected customer, include it to show the affaire list
      if (searchContext.selectedCustomer) {
        navState.selectedCustomer = searchContext.selectedCustomer;
      }

      navigate('/admin/search', { state: navState });
    } else {
      navigate(backUrl);
    }
  };

  return (
    <ExpertiseDetailPage
      dossierId={reference || ''}
      backUrl={backUrl}
      showActions={true}
      variant="full"
      onBack={handleBack}
    />
  );
}

// Inner component that has access to UnreadProvider context
const AdminLayoutContent: React.FC<{
  tabsWithBadge: any[]
  getBadgeValue: (tabId: string) => string | number | undefined
}> = ({ tabsWithBadge, getBadgeValue }) => {
  return (
    <MainLayout
      tabs={tabsWithBadge}
      navigationItems={adminNavigation}
      getBadgeValue={getBadgeValue}
    >
      <Routes>
          {/* Expertise detail page (not in navigation) */}
          <Route
            path="expertise/:reference"
            element={
              <ExpertiseDetailPageWrapper />
            }
          />

          {/* Tab routes */}
          <Route path="pipeline" element={<PipelineTab />} />
          <Route path="tasks" element={<TasksTab />} />
          <Route path="user-management" element={<UserManagementTab />} />
          <Route path="search" element={<SearchTab />} />
          <Route path="payment" element={<PaymentTab />} />
          <Route path="facturation" element={<FacturationTab />} />
          <Route path="boards" element={<BoardsTab />} />
          <Route path="expert" element={<ExpertTab />} />
          <Route path="automatisation" element={<AutomatisationTab />} />
          <Route path="ia" element={<IATab />} />
          <Route path="agent-lea" element={<AgentLeaTab />} />
          <Route path="lea-v3" element={<LeaV3Tab />} />
          <Route path="nouveau-dossier" element={<CreateDossierWizard />} />
          <Route path="mes-dossiers" element={<NewDossiersTab />} />
          <Route path="affaire/:affaireId" element={<AffaireDetailPage />} />
          <Route path="tracker/:affaireId" element={<TrackerDetailPage />} />
          <Route path="tracker" element={<TrackerTab />} />
          <Route path="emails" element={<EmailsTab />} />
          <Route path="sms" element={<SMSTab />} />
          <Route path="ticket" element={<TicketTab />} />
          <Route path="monday-meeting" element={<MondayMeetingTab />} />
          <Route path="mon-espace" element={<MonEspaceTab />} />
          <Route path="equipe" element={<EquipeTab />} />
          <Route path="bureau-activity" element={<BureauActivityTab />} />
          <Route path="voip" element={<VoipTab />} />
          <Route path="appels" element={<AppelsTab />} />
          <Route path="settings" element={<SettingsTab />} />
          <Route path="whats-new" element={<WhatsNewTab />} />
          {/* Default route - redirect to whats-new */}
          <Route path="/" element={<Navigate to="whats-new" replace />} />
          <Route index element={<Navigate to="whats-new" replace />} />
        </Routes>
      </MainLayout>
  );
}

export const AdminLayout: React.FC = () => {
  const { unreadCount, refetch } = useUnreadCount();
  const { unpaidCount } = useUnpaidPaymentCount();
  const { unreadCount: trackerUnreadCount, refetch: refetchTrackerUnread } = useTrackerUnreadCount();
  const { mentionCount: ticketMentionCount } = useTicketMentionCount();
  const { unreadCount: smsUnreadCount, refetch: refetchSMSUnread } = useSMSUnreadCount();
  const { pendingCount: agentPendingCount } = useAgentPendingCount();
  const { badgeCount: emailBadgeCount } = useEmailTriageBadgeCount();
  const { unreadCount: voicemailUnreadCount } = useVoicemailUnreadCount();
  const { count: unvalidatedWeeksCount } = useUnvalidatedWeeksCount();
  const [tabsWithBadge, setTabsWithBadge] = useState(adminTabs);

  // Enable tracker notifications globally
  useTrackerNotifications({ enabled: true });

  // Callback to get badge value for a tab ID (used by grouped navigation)
  const getBadgeValue = useCallback(
    (tabId: string): string | number | undefined => {
      if (tabId === 'tasks' && unreadCount > 0) {
        return unreadCount;
      }
      if (tabId === 'payment' && unpaidCount > 0) {
        return unpaidCount;
      }
      if (tabId === 'tracker' && trackerUnreadCount > 0) {
        return trackerUnreadCount;
      }
      if (tabId === 'ticket' && ticketMentionCount > 0) {
        return ticketMentionCount;
      }
      if (tabId === 'sms' && smsUnreadCount > 0) {
        return smsUnreadCount;
      }
      if (tabId === 'agent-lea' && agentPendingCount > 0) {
        return agentPendingCount;
      }
      if (tabId === 'emails' && emailBadgeCount > 0) {
        return emailBadgeCount;
      }
      if (tabId === 'mon-espace' && unvalidatedWeeksCount > 0) {
        return unvalidatedWeeksCount;
      }
      if (tabId === 'appels' && voicemailUnreadCount > 0) {
        return voicemailUnreadCount;
      }
      return undefined;
    },
    [unreadCount, unpaidCount, trackerUnreadCount, ticketMentionCount, smsUnreadCount, agentPendingCount, emailBadgeCount, unvalidatedWeeksCount, voicemailUnreadCount]
  );

  useEffect(() => {
    // Update tabs with badge counts (for flat navigation fallback)
    const updatedTabs = adminTabs.map((tab) => {
      // Tasks badge
      if (tab.id === 'tasks' && unreadCount > 0) {
        return {
          ...tab,
          badge: {
            value: unreadCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Payment badge
      if (tab.id === 'payment' && unpaidCount > 0) {
        return {
          ...tab,
          badge: {
            value: unpaidCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Tracker badge
      if (tab.id === 'tracker' && trackerUnreadCount > 0) {
        return {
          ...tab,
          badge: {
            value: trackerUnreadCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Ticket badge (for @mentions)
      if (tab.id === 'ticket' && ticketMentionCount > 0) {
        return {
          ...tab,
          badge: {
            value: ticketMentionCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // SMS badge (for unprocessed messages)
      if (tab.id === 'sms' && smsUnreadCount > 0) {
        return {
          ...tab,
          badge: {
            value: smsUnreadCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Agent Léa badge (pending plans)
      if (tab.id === 'agent-lea' && agentPendingCount > 0) {
        return {
          ...tab,
          badge: {
            value: agentPendingCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Emails badge (interesting + new_affaire)
      if (tab.id === 'emails' && emailBadgeCount > 0) {
        return {
          ...tab,
          badge: {
            value: emailBadgeCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Mon Espace badge — past weeks waiting for the user to validate hours
      if (tab.id === 'mon-espace' && unvalidatedWeeksCount > 0) {
        return {
          ...tab,
          badge: {
            value: unvalidatedWeeksCount.toString(),
            variant: 'warning' as const
          }
        };
      }
      return tab;
    });
    setTabsWithBadge(updatedTabs);
  }, [unreadCount, unpaidCount, trackerUnreadCount, ticketMentionCount, smsUnreadCount, agentPendingCount, emailBadgeCount, unvalidatedWeeksCount]);

  return (
    <UnreadProvider refetchUnreadCount={refetch}>
      <TrackerUnreadProvider refetchUnreadCount={refetchTrackerUnread}>
        <SMSUnreadProvider refetchUnreadCount={refetchSMSUnread}>
          <AdminLayoutContent tabsWithBadge={tabsWithBadge} getBadgeValue={getBadgeValue} />
        </SMSUnreadProvider>
      </TrackerUnreadProvider>
    </UnreadProvider>
  );
}
